import {
  normalizePassiveListenerOptions,
  supportsPassiveEventListeners,
} from '@angular/cdk/platform';
import { Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Subject } from 'rxjs';
import { FilterItemDto } from 'src/core/models/request/filter-item.dto';
import { BaseDropdownSelectorScrollComponent } from '../base-dropdown-selector-scroll/base-dropdown-selector-scroll.component';

@Component({
  selector: 'ca-base-dropdown-selector',
  templateUrl: './base-dropdown-selector.component.html',
  styleUrls: ['./base-dropdown-selector.component.scss'],
  standalone: false,
})
export abstract class BaseDropdownSelectorComponent<TItem>
  extends BaseDropdownSelectorScrollComponent
  implements OnDestroy, OnInit
{
  @Input()
  placeholder: string;

  @Input()
  multiple: boolean = false;

  @Input()
  closeOnSelect: boolean = true;

  @Input()
  bringToFront: boolean = false;

  @Input()
  removeClassFromNgSelect: string;

  @Input()
  addClassToNgSelect: string;

  @Input()
  addClassToDropdown: string;

  @Input()
  itemsPerPage: number = 9999;

  @Input()
  dropdownMarginRight: number | null = null;

  @Input()
  set filters(value: FilterItemDto[]) {
    this._filters = value;
    this.pageIndex = 0;

    if (!this.initialLoad) {
      this.load();
    }
  }

  get filters(): FilterItemDto[] {
    return this._filters;
  }

  get classList(): string {
    let result = this.classListArray.join(' ');

    if (this._mutationClassList) {
      result = result + ' ' + this._mutationClassList.value;
    }

    return result;
  }

  get items(): TItem[] {
    return this._items;
  }

  set items(value: TItem[]) {
    this._items = value;
    this.ngSelect?.detectChanges();
  }

  changes: MutationObserver;
  classListArray: string[] = [];
  pageIndex: number = 0;
  totalCount: number = 0;
  loading: boolean = false;
  quickSearchTerm: string = '';
  initialLoad: boolean = true;
  value: any;
  isDisabled: boolean = false;
  quickSearchTermInput$ = new Subject<string>();

  protected abstract load(): void;

  private _filters: FilterItemDto[] = [];
  private _mutationClassList: DOMTokenList;
  private _items: TItem[] = [];

  constructor(protected elementRef: ElementRef, protected ngZone: NgZone) {
    super(ngZone);

    this.changes = new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach((mutation: MutationRecord) => {
        const nativeElement = this.elementRef.nativeElement as HTMLElement;

        this._mutationClassList = nativeElement.classList;
      });
    });

    this.changes.observe(this.elementRef.nativeElement, {
      attributeFilter: ['class'],
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnDestroy(): void {
    this.changes.disconnect();
    super.ngOnDestroy();
  }

  onChange(args: any) {}

  onTouched() {}

  onOpen(eventArgs: any): void {
    setTimeout(() => {
      const dropdownId = this.ngSelect.dropdownId;
      const dropdownEl = document.getElementById(dropdownId);

      if (this.dropdownMarginRight !== null) {
        const left = dropdownEl.style.left.replace('px', '');
        const fLeft = parseFloat(left);
        const newFLeft = fLeft - 92;

        dropdownEl.style.left = newFLeft + 'px';
      }

      dropdownEl.classList.add('ca-selector-dropdown-panel');

      if (this.bringToFront) {
        dropdownEl.classList.add('bring-to-front');
      }

      if (this.addClassToDropdown) {
        dropdownEl.classList.add(this.addClassToDropdown);
      }

      if (this.removeClassFromNgSelect) {
        var classListToBeRemoved = this.removeClassFromNgSelect.split(' ');
        this.ngSelect.element.classList.remove(...classListToBeRemoved);
      }

      if (this.addClassToNgSelect) {
        var classListToBeAdded = this.addClassToNgSelect.split(' ');
        this.ngSelect.element.classList.add(...classListToBeAdded);
      }
    }, 1);

    if (this.initialLoad) {
      this.load();
      this.initialLoad = false;
    }
  }

  onClear(eventArgs: any): void {
    this.quickSearchTerm = '';
    this.pageIndex = 0;

    this.load();
  }

  onPageChange(eventArgs: PageEvent): void {
    this.pageIndex = eventArgs.pageIndex;

    this.load();
  }

  writeValue(obj: any): void {
    this.value = obj;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }
}
